import React, { useContext } from 'react';
import { Form, FormikProps, ErrorMessage } from 'formik';
import {
    ResponseError,
    SubmitButton,
    AsyncSelectField,
    InputField,
    MultiSelectField,
    AsyncMultiSelectField,
} from '@nintendo/forms';
import moment from 'moment-timezone';
import { Container, Col, Row, Button } from 'react-bootstrap';
import Tooltip from '@nintendo/forms/lib/js/components/common/Tooltip';
import ConfigContext from '../../../contexts/ConfigContext';
import {
    AmStatusType,
    AM_NUMBER_REGEX,
    DATE_ONLY_FORMAT,
    SERVER_TIMEZONE,
} from '../../../utils/Constants';
import { getAmUsers, getContractSignerUsers } from '../../../api/amUserApi';
import { getProjectOrganizations } from '../../../api/amOrganizationApi';
import useAsyncWithState from '../../../hooks/useAsyncWithState';
import { ProjectOrganizationType } from '../../../api/amOrganizationApiTypes';
import { getTemplates } from '../../../api/amTemplateApi';
import { AmTemplateType } from '../../../api/amTemplateApiTypes';
import { SelectOptionType } from '../../common/selectOptionTypes';
import { noOptionsHandler, formatNameTitle } from '../../../utils/displayUtils';
import CurrencySearchField from './CurrencySearchField';
import DateSearchField from '../../common/DateSearchField';

export interface ActionMemoSearchFormType {
    amNumberSearchValue: string;
    amNumber: string;
    supNumber: string;
    amNumberFrom: string;
    amNumberTo: string;
    budgetAmountFrom: string;
    budgetAmountTo: string;
    textSearchValues: string;
    amStatuses: string[];
    amCreator: string;
    amCreatorName: string;
    currentApproverId: string;
    currentApproverName: string;
    projectManagerId: string;
    projectManagerName: string;
    contractSignerId: string;
    contractSignerName: string;
    organizationIds: string[];
    organizationNames: string[];
    dateFrom: string | undefined;
    dateTo: string | undefined;
    templateCodes: string[];
}

export interface ActionMemoSearchFormProps extends FormikProps<ActionMemoSearchFormType> {
    responseError: string;
    setResponseError: (errorMessage: string) => void;
    isfilterExpanded: boolean;
    searching: boolean;
}

const ActionMemoSearchForm = (props: ActionMemoSearchFormProps) => {
    const config = useContext(ConfigContext);

    const statusOptions = Object.keys(AmStatusType).map((key) => ({
        value: (AmStatusType as { [key: string]: string })[key],
        label: (AmStatusType as { [key: string]: string })[key],
    }));

    const { value: orgs } = useAsyncWithState(
        () => getProjectOrganizations(config),
        [] as ProjectOrganizationType[],
    );

    const { value: templates } = useAsyncWithState(
        () => getTemplates(config),
        [] as AmTemplateType[],
    );

    const { values, errors, touched, isSubmitting, setFieldValue, isfilterExpanded, setValues } =
        props;

    return (
        <>
            <div className="desktop-hide">
                <Form>
                    <ResponseError errorMessage={props.responseError} />
                    <Container fluid>
                        <Row>
                            <div className="d-xl-none input-group mb-3">
                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder="Search by AM# or Keyword"
                                    defaultValue={values.amNumber || values.textSearchValues}
                                    onChange={(e) => {
                                        const value = e.target.value.trim();
                                        if (value) {
                                            if (value.match(AM_NUMBER_REGEX)) {
                                                const amNumberValue = value.split('-');
                                                setFieldValue('amNumber', amNumberValue[0]);
                                                if (amNumberValue.length > 1) {
                                                    setFieldValue('supNumber', amNumberValue[1]);
                                                }
                                                setFieldValue('textSearchValues', '');
                                            } else {
                                                setFieldValue('amNumber', '');
                                                setFieldValue('textSearchValues', value);
                                            }
                                        } else {
                                            setFieldValue('amNumber', '');
                                            setFieldValue('textSearchValues', '');
                                        }
                                    }}
                                />
                                <SubmitButton
                                    className="btn-primary"
                                    disabled={props.searching}
                                    isSubmitting={isSubmitting}
                                    buttonText={<i className="fas fa-search-plus" />}
                                />
                            </div>
                        </Row>
                    </Container>
                </Form>
            </div>
            <div className="mobile-hide">
                <Form>
                    <ResponseError errorMessage={props.responseError} />
                    <Container fluid>
                        <Row>
                            <Col>
                                <InputField
                                    label="AM#"
                                    name="amNumberSearchValue"
                                    touched={touched.amNumberSearchValue}
                                    errors={errors.amNumberSearchValue}
                                    testId="amNumberSearchValue"
                                    inputProps={{
                                        onChange: (e) => {
                                            const value = e.target.value.trim();
                                            setFieldValue('amNumberSearchValue', value);

                                            if (value && value.match(AM_NUMBER_REGEX)) {
                                                const amNumberValues = value.split('-');
                                                setFieldValue('amNumber', amNumberValues[0]);
                                                if (amNumberValues.length > 1) {
                                                    setFieldValue('supNumber', amNumberValues[1]);
                                                }
                                            } else {
                                                setFieldValue('amNumber', '');
                                                setFieldValue('supNumber', '');
                                            }
                                        },
                                    }}
                                    labelCssClass="col-sm-3"
                                    contentCssClass="col-sm-9"
                                />
                                {isfilterExpanded && (
                                    <>
                                        <div
                                            className={`form-group row ${
                                                touched.budgetAmountFrom &&
                                                errors.budgetAmountFrom &&
                                                'has-error'
                                            } ${
                                                touched.budgetAmountTo &&
                                                errors.budgetAmountTo &&
                                                'has-error'
                                            }`}
                                        >
                                            <div className="col-sm-3 control-label breakdown-label">
                                                Budget &nbsp;
                                            </div>
                                            <div className="col-sm-9">
                                                <div
                                                    className={`form-group row mb-0 ${
                                                        touched.budgetAmountFrom &&
                                                        errors.budgetAmountFrom &&
                                                        'has-error'
                                                    } ${
                                                        touched.budgetAmountTo &&
                                                        errors.budgetAmountTo &&
                                                        'has-error'
                                                    }`}
                                                >
                                                    <CurrencySearchField
                                                        label="Budget From"
                                                        name="budgetAmountFrom"
                                                        touched={touched.budgetAmountFrom}
                                                        errors={errors.budgetAmountFrom}
                                                        contentCssClass="col-sm-6"
                                                        placeholder="min"
                                                        testId="budgetAmountFrom"
                                                    />
                                                    <CurrencySearchField
                                                        label="Budget To"
                                                        name="budgetAmountTo"
                                                        touched={touched.budgetAmountTo}
                                                        errors={errors.budgetAmountTo}
                                                        contentCssClass="col-sm-6"
                                                        placeholder="max"
                                                        testId="budgetAmountTo"
                                                    />
                                                </div>

                                                <ErrorMessage
                                                    name="budgetAmountFrom"
                                                    className="text-danger"
                                                    component="small"
                                                />
                                                <ErrorMessage
                                                    name="budgetAmountTo"
                                                    className="text-danger"
                                                    component="small"
                                                />
                                            </div>
                                        </div>

                                        <div
                                            className={`form-group row ${
                                                touched.dateFrom && errors.dateFrom && 'has-error'
                                            } ${touched.dateTo && errors.dateTo && 'has-error'}`}
                                        >
                                            <div className="col-sm-3 control-label breakdown-label">
                                                Date &nbsp;
                                                <Tooltip
                                                    text="Searches both creation and approval dates"
                                                    name="dateFrom"
                                                />
                                            </div>
                                            <div className="col-sm-9">
                                                <div
                                                    className={`form-group row mb-0 ${
                                                        touched.dateFrom &&
                                                        errors.dateFrom &&
                                                        'has-error'
                                                    } ${
                                                        touched.dateTo &&
                                                        errors.dateTo &&
                                                        'has-error'
                                                    }`}
                                                >
                                                    <DateSearchField
                                                        label="Date From"
                                                        name="dateFrom"
                                                        flatpickrProps={{
                                                            options: {
                                                                altInputClass: 'form-control',
                                                                altFormat: DATE_ONLY_FORMAT,
                                                                enableTime: false,
                                                                dateFormat: DATE_ONLY_FORMAT,
                                                                defaultDate: '',
                                                            },
                                                            value: values.dateFrom
                                                                ? moment(values.dateFrom).format(
                                                                      DATE_ONLY_FORMAT,
                                                                  )
                                                                : undefined,
                                                        }}
                                                        timezone={SERVER_TIMEZONE}
                                                        touched={touched.dateFrom}
                                                        errors={errors.dateFrom}
                                                        contentCssClass="col-sm-6"
                                                        placeholder="from"
                                                        testId="dateFrom"
                                                    />
                                                    <DateSearchField
                                                        label="Date To"
                                                        name="dateTo"
                                                        flatpickrProps={{
                                                            options: {
                                                                altInputClass: 'form-control',
                                                                altFormat: DATE_ONLY_FORMAT,
                                                                enableTime: false,
                                                                dateFormat: DATE_ONLY_FORMAT,
                                                                defaultDate: '',
                                                            },
                                                            value: values.dateTo
                                                                ? moment(values.dateTo).format(
                                                                      DATE_ONLY_FORMAT,
                                                                  )
                                                                : undefined,
                                                        }}
                                                        timezone={SERVER_TIMEZONE}
                                                        touched={touched.dateTo}
                                                        errors={errors.dateTo}
                                                        contentCssClass="col-sm-6"
                                                        placeholder="to"
                                                        testId="dateTo"
                                                    />
                                                </div>

                                                <ErrorMessage
                                                    name="dateFrom"
                                                    className="text-danger"
                                                    component="small"
                                                />
                                                <ErrorMessage
                                                    name="dateTo"
                                                    className="text-danger"
                                                    component="small"
                                                />
                                            </div>
                                        </div>
                                    </>
                                )}
                            </Col>
                            <Col>
                                <InputField
                                    label="Keywords"
                                    name="textSearchValues"
                                    touched={touched.textSearchValues}
                                    errors={errors.textSearchValues}
                                    labelCssClass="col-sm-4"
                                    contentCssClass="col-sm-8"
                                    testId="textSearchValues"
                                />
                                {isfilterExpanded && (
                                    <>
                                        <AsyncSelectField
                                            label="Creator"
                                            name="amCreator"
                                            touched={touched.amCreator}
                                            errors={errors.amCreator}
                                            asyncSelectProps={{
                                                isClearable: true,

                                                value: values.amCreator
                                                    ? {
                                                          value: values.amCreator.toString(),
                                                          label: values.amCreatorName,
                                                      }
                                                    : null,
                                                onChange: (option) => {
                                                    if (option) {
                                                        setFieldValue(
                                                            'amCreatorName',
                                                            (option as SelectOptionType).label,
                                                        );
                                                        setFieldValue(
                                                            'amCreator',
                                                            (option as SelectOptionType).value,
                                                        );
                                                    } else {
                                                        setFieldValue('amCreator', null);
                                                        setFieldValue('amCreatorName', null);
                                                    }
                                                },
                                                loadOptions: async (inputValue: string) => {
                                                    if (inputValue.length > 2) {
                                                        const users = await getAmUsers(config, {
                                                            name: inputValue,
                                                            amCreator: true,
                                                        });
                                                        if (users) {
                                                            return users.map((user) => ({
                                                                value: user.personId.toString(),
                                                                label: formatNameTitle(
                                                                    user.preferredName,
                                                                    user.title,
                                                                ),
                                                            }));
                                                        }
                                                    }
                                                    return [];
                                                },
                                                noOptionsMessage: noOptionsHandler,
                                            }}
                                            labelCssClass="col-sm-4"
                                            contentCssClass="col-sm-8"
                                            testId="amCreatorName"
                                        />
                                        <AsyncSelectField
                                            label="Project Manager"
                                            name="projectManagerId"
                                            touched={touched.projectManagerId}
                                            errors={errors.projectManagerId}
                                            asyncSelectProps={{
                                                isClearable: true,
                                                loadOptions: async (inputValue: string) => {
                                                    if (inputValue.length > 2) {
                                                        const users = await getAmUsers(config, {
                                                            name: inputValue,
                                                            projectManager: true,
                                                        });
                                                        if (users) {
                                                            return users.map((user) => ({
                                                                value: user.personId.toString(),
                                                                label: formatNameTitle(
                                                                    user.preferredName,
                                                                    user.title,
                                                                ),
                                                            }));
                                                        }
                                                    }
                                                    return [];
                                                },
                                                value: values.projectManagerId
                                                    ? {
                                                          value: values.projectManagerId.toString(),
                                                          label: values.projectManagerName,
                                                      }
                                                    : null,

                                                onChange: (option) => {
                                                    if (option) {
                                                        setFieldValue(
                                                            'projectManagerName',
                                                            (option as SelectOptionType).label,
                                                        );
                                                        setFieldValue(
                                                            'projectManagerId',
                                                            (option as SelectOptionType).value,
                                                        );
                                                    } else {
                                                        setFieldValue('projectManagerId', null);
                                                        setFieldValue('projectManagerName', null);
                                                    }
                                                },
                                                noOptionsMessage: noOptionsHandler,
                                            }}
                                            labelCssClass="col-sm-4"
                                            contentCssClass="col-sm-8"
                                            testId="projectManagerId"
                                        />

                                        <AsyncSelectField
                                            label="Current Approver"
                                            name="currentApproverId"
                                            touched={touched.currentApproverId}
                                            errors={errors.currentApproverId}
                                            asyncSelectProps={{
                                                isClearable: true,
                                                loadOptions: async (inputValue: string) => {
                                                    if (inputValue.length > 2) {
                                                        const users = await getAmUsers(config, {
                                                            name: inputValue,
                                                            approver: true,
                                                        });
                                                        if (users) {
                                                            return users.map((user) => ({
                                                                value: user.personId.toString(),
                                                                label: formatNameTitle(
                                                                    user.preferredName,
                                                                    user.title,
                                                                ),
                                                            }));
                                                        }
                                                    }
                                                    return [];
                                                },
                                                value: values.currentApproverId
                                                    ? {
                                                          value: values.currentApproverId.toString(),
                                                          label: values.currentApproverName,
                                                      }
                                                    : null,

                                                onChange: (option) => {
                                                    if (option) {
                                                        setFieldValue(
                                                            'currentApproverName',
                                                            (option as SelectOptionType).label,
                                                        );
                                                        setFieldValue(
                                                            'currentApproverId',
                                                            (option as SelectOptionType).value,
                                                        );
                                                    } else {
                                                        setFieldValue('currentApproverId', null);
                                                        setFieldValue('currentApproverName', null);
                                                    }
                                                },
                                                noOptionsMessage: noOptionsHandler,
                                            }}
                                            labelCssClass="col-sm-4"
                                            contentCssClass="col-sm-8"
                                            testId="currentApproverId"
                                        />
                                    </>
                                )}
                            </Col>
                            <Col>
                                <MultiSelectField
                                    selectProps={
                                        {
                                            closeMenuOnSelect: false,
                                        } as any
                                    }
                                    selectedOptions={
                                        values.amStatuses
                                            ? statusOptions
                                                  .filter((statusOption) =>
                                                      values.amStatuses.includes(
                                                          statusOption.value,
                                                      ),
                                                  )
                                                  .map((statusOption) => ({
                                                      label: statusOption.label,
                                                      value: statusOption.value,
                                                  }))
                                            : []
                                    }
                                    options={statusOptions}
                                    label="Status"
                                    name="amStatuses"
                                    touched={touched.amStatuses}
                                    errors={errors.amStatuses}
                                    labelCssClass="col-sm-4"
                                    contentCssClass="col-sm-8"
                                    testId="amStatuses"
                                />
                                {isfilterExpanded && (
                                    <>
                                        <AsyncMultiSelectField
                                            label="Organization"
                                            name="organizationIds"
                                            touched={touched.organizationIds}
                                            errors={errors.organizationIds}
                                            asyncSelectProps={{
                                                cacheOptions: true,
                                                loadOptions: async (inputValue: string) => {
                                                    if (inputValue.length > 2) {
                                                        return orgs
                                                            .filter((organization) =>
                                                                organization.name
                                                                    .toUpperCase()
                                                                    .includes(
                                                                        inputValue.toUpperCase(),
                                                                    ),
                                                            )
                                                            .map((org) => ({
                                                                value: org.id.toString(),
                                                                label: org.name,
                                                            }));
                                                    }
                                                    return [];
                                                },
                                                onChange: (options) => {
                                                    if (options) {
                                                        setFieldValue(
                                                            'organizationIds',
                                                            (options as any).map(
                                                                (option: SelectOptionType) =>
                                                                    option.value,
                                                            ),
                                                        );
                                                        setFieldValue(
                                                            'organizationNames',
                                                            (options as any).map(
                                                                (option: SelectOptionType) =>
                                                                    option.label,
                                                            ),
                                                        );
                                                    } else {
                                                        setFieldValue('organizationIds', []);
                                                        setFieldValue('organizationNames', []);
                                                    }
                                                },
                                                value: values.organizationIds
                                                    ? values.organizationIds.map((org, index) => ({
                                                          label: values.organizationNames[index],
                                                          value: org.toString(),
                                                      }))
                                                    : [],
                                                noOptionsMessage: noOptionsHandler,
                                            }}
                                            tooltip="To get a list of organizations,<br/>try typing one of these company names:<br/>HFI, NMI, NOA, NOCL, NST, NTD, Retro"
                                            labelCssClass="col-sm-4"
                                            contentCssClass="col-sm-8"
                                        />
                                        <AsyncMultiSelectField
                                            label="Template"
                                            name="templateCodes"
                                            touched={touched.templateCodes}
                                            errors={errors.templateCodes}
                                            asyncSelectProps={{
                                                cacheOptions: true,
                                                loadOptions: async (inputValue: string) => {
                                                    if (inputValue.length > 2) {
                                                        return templates
                                                            .filter((template) =>
                                                                `${template.code} (${template.description})`
                                                                    .toUpperCase()
                                                                    .includes(
                                                                        inputValue.toUpperCase(),
                                                                    ),
                                                            )
                                                            .map((template) => ({
                                                                value: template.code,
                                                                label: `${template.code} (${template.description})`,
                                                            }));
                                                    }
                                                    return [];
                                                },
                                                value: values.templateCodes
                                                    ? values.templateCodes.map((code) => ({
                                                          label: code,
                                                          value: code,
                                                      }))
                                                    : [],
                                                noOptionsMessage: noOptionsHandler,
                                            }}
                                            tooltip="To get a list of templates, <br/>try typing one of these prefixes:<br/>NOA, NOCL, NST, NTD, RETRO"
                                            labelCssClass="col-sm-4"
                                            contentCssClass="col-sm-8"
                                        />
                                        <AsyncSelectField
                                            label="Contract Signer"
                                            name="contractSignerId"
                                            touched={touched.contractSignerId}
                                            errors={errors.contractSignerId}
                                            asyncSelectProps={{
                                                isClearable: true,
                                                defaultOptions: true,
                                                loadOptions: async (inputValue: string) => {
                                                    const users = await getContractSignerUsers(
                                                        config,
                                                        inputValue,
                                                    );
                                                    if (users) {
                                                        return users.map((user) => ({
                                                            value: user.personId.toString(),
                                                            label: formatNameTitle(
                                                                user.preferredName,
                                                                user.title,
                                                            ),
                                                        }));
                                                    }
                                                    return [];
                                                },
                                                value: values.contractSignerId
                                                    ? {
                                                          value: values.contractSignerId.toString(),
                                                          label: values.contractSignerName,
                                                      }
                                                    : null,

                                                onChange: (option) => {
                                                    if (option) {
                                                        setFieldValue(
                                                            'contractSignerName',
                                                            (option as SelectOptionType).label,
                                                        );
                                                        setFieldValue(
                                                            'contractSignerId',
                                                            (option as SelectOptionType).value,
                                                        );
                                                    } else {
                                                        setFieldValue('contractSignerId', null);
                                                        setFieldValue('contractSignerName', null);
                                                    }
                                                },
                                                noOptionsMessage: noOptionsHandler,
                                            }}
                                            labelCssClass="col-sm-4"
                                            contentCssClass="col-sm-8"
                                            testId="contractSignerId"
                                        />
                                    </>
                                )}
                            </Col>
                            <Col
                                xs="1"
                                className="align-self-center align-self-middle text-nowrap mb-3"
                            >
                                <SubmitButton
                                    className="btn-primary"
                                    disabled={props.searching}
                                    isSubmitting={isSubmitting}
                                    buttonText={<i className="fas fa-search-plus" />}
                                />
                                <Button
                                    variant="outline-primary"
                                    className="ml-1"
                                    onClick={() => {
                                        setValues({
                                            amNumberSearchValue: '',
                                            amNumber: '',
                                            supNumber: '',
                                            amNumberFrom: '',
                                            amNumberTo: '',
                                            budgetAmountFrom: '',
                                            budgetAmountTo: '',
                                            textSearchValues: '',
                                            amStatuses: [],
                                            amCreator: '',
                                            amCreatorName: '',
                                            projectManagerId: '',
                                            projectManagerName: '',
                                            organizationIds: [],
                                            dateFrom: undefined,
                                            dateTo: undefined,
                                            templateCodes: [],
                                            organizationNames: [],
                                            currentApproverId: '',
                                            currentApproverName: '',
                                            contractSignerId: '',
                                            contractSignerName: '',
                                        });
                                    }}
                                >
                                    <i className="fas fa-backspace" />
                                </Button>
                            </Col>
                        </Row>
                    </Container>
                </Form>
            </div>
        </>
    );
};

export default ActionMemoSearchForm;
